import { useEffect, useState } from "react"
import type { ReactNode } from "react"
import { api } from "../api/client.ts"
import type { CaseItem, ItemMaster } from "../api/types.ts"
import { useApiQuery, useAsyncAction, useDebounced } from "../hooks/index.ts"
import { useI18n } from "../i18n/index.tsx"
import { hsCodeDigits } from "../lib/hs-code.ts"
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  useToast,
} from "../ui/index.ts"

type Draft = {
  itemId: number | null
  description: string
  hsCode: string
  originCountry: string
  qty: string
  unitPrice: string
}

function toDraft(item: CaseItem): Draft {
  return {
    itemId: item.itemId,
    description: item.description,
    hsCode: item.hsCode,
    originCountry: item.originCountry ?? "",
    qty: String(item.qty),
    unitPrice: item.unitPrice,
  }
}

/** 글자를 칠 때마다 마스터를 찾지 않는다. 멎으면 그때 한 번. */
const MASTER_SEARCH_DEBOUNCE_MS = 200

/** 소수 둘째 자리 정수 계산. 서버의 lineAmount 와 같은 방식이다. */
function amountOf(qty: string, unitPrice: string): string {
  if (!/^\d+$/.test(qty) || !/^\d+(\.\d{1,2})?$/.test(unitPrice)) return "—"
  const [whole, fraction = ""] = unitPrice.split(".")
  const minor = BigInt(whole) * 100n + BigInt((fraction + "00").slice(0, 2))
  const total = minor * BigInt(qty)
  return `${total / 100n}.${String(total % 100n).padStart(2, "0")}`
}

export function ItemsEditor({
  caseId,
  items,
  editable,
  onSaved,
  headerActions,
}: {
  caseId: number
  items: CaseItem[]
  editable: boolean
  onSaved: () => void
  /** 카드 머리 오른쪽에 함께 놓을 것(인보이스 내려받기). 편집과 상관없이 늘 보인다. */
  headerActions?: ReactNode
}) {
  const { t } = useI18n()
  const toast = useToast()
  const [drafts, setDrafts] = useState<Draft[]>(items.map(toDraft))
  const [search, setSearch] = useState("")
  const [dirty, setDirty] = useState(false)
  const save = useAsyncAction()

  useEffect(() => {
    setDrafts(items.map(toDraft))
    setDirty(false)
  }, [items])

  // 품목 마스터는 고칠 수 있을 때만 찾는다. 오류는 보여 주지 않고 빈 목록으로 둔다
  // — 여기서 못 찾아도 품명을 직접 칠 수 있다.
  const settledSearch = useDebounced(search, MASTER_SEARCH_DEBOUNCE_MS)
  const masterQuery = useApiQuery<{ items: ItemMaster[] }>(
    editable ? `/api/item-masters?q=${encodeURIComponent(settledSearch)}` : null,
  )
  const masters = masterQuery.error ? [] : (masterQuery.data?.items ?? [])

  function change(index: number, patch: Partial<Draft>) {
    setDrafts((current) =>
      current.map((draft, i) => (i === index ? { ...draft, ...patch } : draft)),
    )
    setDirty(true)
  }

  function addFromMaster(master: ItemMaster) {
    setDrafts((current) => [
      ...current,
      {
        itemId: master.id,
        description: master.nameEn,
        hsCode: master.hsCode,
        originCountry: master.originCountry ?? "",
        qty: "1",
        unitPrice: master.defaultUnitPrice ?? "0.00",
      },
    ])
    setDirty(true)
  }

  function addManual() {
    setDrafts((current) => [
      ...current,
      { itemId: null, description: "", hsCode: "", originCountry: "", qty: "1", unitPrice: "0.00" },
    ])
    setDirty(true)
  }

  function remove(index: number) {
    setDrafts((current) => current.filter((_, i) => i !== index))
    setDirty(true)
  }

  async function submit() {
    const ok = await save.run(() =>
      api.put(`/api/cases/${caseId}/items`, {
        items: drafts.map((draft) => ({
          itemId: draft.itemId,
          description: draft.description || null,
          hsCode: draft.hsCode || null,
          originCountry: draft.originCountry || null,
          qty: Number(draft.qty),
          unitPrice: draft.unitPrice,
        })),
      }),
    )
    if (!ok) return
    setDirty(false)
    toast(t("common.saved"))
    onSaved()
  }

  const total = drafts.reduce((sum, draft) => {
    const amount = amountOf(draft.qty, draft.unitPrice)
    return amount === "—" ? sum : sum + Number(amount)
  }, 0)

  return (
    <>
      <ErrorNote error={save.error} />

      <Card padded={false}>
        <CardHeader
          title={t("caseDetail.items")}
          icon="list"
          description={t("items.count", { count: drafts.length })}
          actions={
            <>
              {editable ? (
                <>
                  <Button size="sm" icon="plus" onClick={addManual}>
                    {t("items.manual")}
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    loading={save.busy}
                    disabled={!dirty}
                    onClick={() => void submit()}
                  >
                    {t("items.save")}
                  </Button>
                </>
              ) : null}
              {headerActions}
            </>
          }
        />

        {drafts.length === 0 ? (
          <EmptyState icon="list" title={t("items.empty")} />
        ) : (
          <div className="table-wrap">
            <table className="table items-table">
              <thead>
                <tr>
                  <th scope="col">{t("items.description")}</th>
                  <th scope="col">{t("items.hsCode")}</th>
                  <th scope="col">{t("items.origin")}</th>
                  <th scope="col" className="num">
                    {t("items.qty")}
                  </th>
                  <th scope="col" className="num">
                    {t("items.unitPrice")}
                  </th>
                  <th scope="col" className="num">
                    {t("items.amount")}
                  </th>
                  {editable ? <th scope="col" /> : null}
                </tr>
              </thead>
              <tbody>
                {drafts.map((draft, index) => (
                  <tr key={index}>
                    <td>
                      {editable ? (
                        <Input
                          aria-label={t("items.description")}
                          value={draft.description}
                          onChange={(event) => change(index, { description: event.target.value })}
                        />
                      ) : (
                        draft.description
                      )}
                    </td>
                    <td>
                      {editable ? (
                        <Input
                          className="items-table__hs"
                          aria-label={t("items.hsCode")}
                          inputMode="numeric"
                          value={draft.hsCode}
                          // 점을 쳐도 들어가지 않는다 — 신고 모양이 숫자만이다(lib/hs-code.ts).
                          onChange={(event) =>
                            change(index, { hsCode: hsCodeDigits(event.target.value) })
                          }
                        />
                      ) : (
                        draft.hsCode
                      )}
                    </td>
                    <td>
                      {editable ? (
                        <Input
                          className="items-table__hs"
                          aria-label={t("items.origin")}
                          value={draft.originCountry}
                          onChange={(event) =>
                            change(index, { originCountry: event.target.value })
                          }
                        />
                      ) : (
                        draft.originCountry || "—"
                      )}
                    </td>
                    <td className="num">
                      {editable ? (
                        <Input
                          className="items-table__qty"
                          aria-label={t("items.qty")}
                          inputMode="numeric"
                          value={draft.qty}
                          onChange={(event) => change(index, { qty: event.target.value })}
                        />
                      ) : (
                        draft.qty
                      )}
                    </td>
                    <td className="num">
                      {editable ? (
                        <Input
                          className="items-table__price"
                          aria-label={t("items.unitPrice")}
                          inputMode="decimal"
                          value={draft.unitPrice}
                          onChange={(event) => change(index, { unitPrice: event.target.value })}
                        />
                      ) : (
                        draft.unitPrice
                      )}
                    </td>
                    <td className="num">{amountOf(draft.qty, draft.unitPrice)}</td>
                    {editable ? (
                      <td>
                        <Button size="sm" variant="ghost" icon="trash" onClick={() => remove(index)}>
                          {t("items.remove")}
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5}>{t("items.total")}</td>
                  <td className="num">{total.toFixed(2)}</td>
                  {editable ? <td /> : null}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      {editable ? (
        <Card padded={false}>
          <CardHeader title={t("items.pickMaster")} icon="tag" />
          <div className="items-search">
            <Field label={t("items.search")} htmlFor="items-search">
              <Input
                id="items-search"
                icon="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </Field>
          </div>

          {masters.length === 0 ? (
            <EmptyState icon="tag" title={t("itemMasters.empty")} />
          ) : (
            <div className="table-wrap masters-wrap">
              <table className="table table--compact">
                <thead>
                  <tr>
                    <th scope="col">{t("itemMasters.code")}</th>
                    <th scope="col">{t("itemMasters.nameKo")}</th>
                    <th scope="col">{t("itemMasters.nameEn")}</th>
                    <th scope="col">{t("itemMasters.hsCode")}</th>
                    <th scope="col" className="num">
                      {t("itemMasters.price")}
                    </th>
                    <th scope="col" />
                  </tr>
                </thead>
                <tbody>
                  {masters.map((master) => (
                    <tr key={master.id}>
                      <td>{master.code}</td>
                      <td>{master.nameKo}</td>
                      <td>{master.nameEn}</td>
                      <td>{master.hsCode}</td>
                      <td className="num">{master.defaultUnitPrice ?? "—"}</td>
                      <td>
                        <Button size="sm" icon="plus" onClick={() => addFromMaster(master)}>
                          {t("items.add")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ) : null}
    </>
  )
}
